import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { jwtDecode } from "jwt-decode";
import { GENERIC_ERROR_MESSAGE, logApiError, logUnexpectedError } from "../Utils/apiError";

const RECAPTCHA_SITE_KEY = "6LdrkZArAAAAAFagTf1DUI77pb59JT81RJTLNHRP";

function RegisterForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const waitForRecaptcha = async () => {
    for (let i = 0; i < 30; i++) {
      if (window.grecaptcha && window.grecaptcha.execute) return true;
      await new Promise((res) => setTimeout(res, 100));
    }
    return false;
  };

  const isPasswordValid = (pwd) => pwd.length >= 8 && /[A-Z]/.test(pwd) && /[0-9]/.test(pwd);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError("");
    setSubmitting(true);

    if (!isPasswordValid(password)) {
      setError("Password must be at least 8 characters, include an uppercase letter and a number.");
      setSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setSubmitting(false);
      return;
    }

    const recaptchaReady = await waitForRecaptcha();
    if (!recaptchaReady) {
      setError("Captcha not loaded. Please refresh and try again.");
      setSubmitting(false);
      return;
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "register" });

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/public/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName, captchaToken: token }),
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.clone().json();
        } catch {
          errorData = {};
        }

        if (response.status === 409 && errorData?.error === "email_not_verified") {
          localStorage.setItem("pendingVerificationEmail", email);
          navigate("/check-email");
          return;
        }

        if (response.status === 409 && errorData?.error === "already_verified") {
          setError("Email already verified. Please log in.");
          return;
        }

        await logApiError(response, "Registration failed");
        setError(GENERIC_ERROR_MESSAGE);
        return;
      }

      const data = await response.json();
      window.gtag?.("event", "register", {
        event_category: "engagement",
      });
      window.gtag?.("event", "sign_up", {
        method: "email",
      });

      if (data.token) {
        const decoded = jwtDecode(data.token);
        const userEmail = decoded.sub;
        const userId = decoded.userId || decoded.id;

        localStorage.removeItem("pendingVerificationEmail");
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(userEmail));
        login(userEmail, userId);

        navigate("/");
      } else {
        localStorage.setItem("pendingVerificationEmail", email);
        navigate("/check-email");
      }
    } catch (err) {
      logUnexpectedError(err, "Registration request failed");
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "InitiateRegistration");
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-5xl items-center">
        <div className="grid w-full gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <section className="order-2 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:order-1 lg:flex lg:flex-col lg:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                Meshum
              </div>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
                Know what every car is really worth.
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600">
                Meshum helps buyers compare listings with real sold vehicle data so they can shop, negotiate, and contact dealers with confidence.
              </p>
            </div>

            <div className="mt-6 grid gap-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Benefit text="Save favorite vehicles" />
                <Benefit text="Compare Meshum Estimates" />
                <Benefit text="View sold comparable vehicles" />
                <Benefit text="Track vehicles you are interested in" />
                <Benefit text="Access market insights while shopping" />
                <Benefit text="Continue searches across devices" />
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <TrustBullet text="Real sold vehicle data" />
                <TrustBullet text="Market-based pricing insights" />
                <TrustBullet text="Nationwide inventory" />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Asking price</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">$29,900</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Meshum Estimate</div>
                    <div className="mt-1 text-lg font-semibold text-blue-700">$27,800</div>
                  </div>
                </div>
                <div className="mt-3 inline-flex rounded-full bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700">
                  Above market
                </div>
              </div>
            </div>
          </section>

          <section className="order-1 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:order-2">
            <div className="mb-6">
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                Meshum
              </div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                Create your Meshum account
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Save cars, compare market insights, and continue your search with confidence.
              </p>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name">
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </Field>
              </div>

              <Field label="Email">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </Field>

              <Field label="Password">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </Field>

              <Field label="Confirm password">
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </Field>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="text-sm font-medium text-slate-900">Password requirements</div>
                <ul className="mt-2 space-y-2 text-sm text-slate-600">
                  <Rule ok={password.length >= 8} text="At least 8 characters" />
                  <Rule ok={/[A-Z]/.test(password)} text="An uppercase letter" />
                  <Rule ok={/[0-9]/.test(password)} text="A number" />
                </ul>
              </div>

              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Creating account..." : "Create account"}
              </button>
            </form>

            <div className="mt-5 text-center text-sm text-slate-600">
              Already have an account?{" "}
              <a href="/login" className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800">
                Login here
              </a>
            </div>

            <p className="mt-6 text-xs leading-5 text-slate-500">
              This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function TrustBullet({ text }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="text-sm font-medium text-slate-900">{text}</div>
    </div>
  );
}

function Benefit({ text }) {
  return (
    <div className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <span className="mt-0.5 text-emerald-600">✓</span>
      <span className="text-sm font-medium text-slate-900">{text}</span>
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={ok ? "text-emerald-700" : "text-rose-700"}>
      {ok ? "✓" : "•"} {text}
    </li>
  );
}

export default RegisterForm;
